// Shared identity-avatar helpers: derive display INITIALS and a DETERMINISTIC
// background color from a string key (user id / name / email). Used by the
// userbar avatar (Layout) and reusable anywhere a small identity chip is shown —
// the SAME scheme everywhere so a given user always looks the same.

// 1–2 uppercase letters. A multi-word name ("Ada Admin") → "AA"; a single name
// ("whisper") → its first two letters ("WH"); an email-only identity
// ("bob@acme.dev") → the first letter of the local part ("B"). Empty → "?".
export function initials(nameOrEmail: string | null | undefined): string {
  const raw = (nameOrEmail ?? '').trim();
  if (!raw) return '?';
  const isEmail = raw.includes('@');
  const base = isEmail ? raw.split('@')[0] : raw;
  const words = base.split(/[\s._\-+]+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) {
    // For an email use a single letter; for a plain name use up to two.
    return words[0].slice(0, isEmail ? 1 : 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

// Deterministic, readable HSL background derived from a key — stable across
// renders/sessions (same key → same color). Saturation/lightness are fixed so
// white text stays legible on every hue.
export function colorFor(key: string | null | undefined): string {
  const s = key ?? '';
  // Simple FNV-ish rolling hash → a hue in [0, 360).
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(h, 31) + s.charCodeAt(i)) >>> 0;
  }
  const hue = h % 360;
  return `hsl(${hue}, 52%, 45%)`;
}
